<template>
  <div>
    <va-modal
      v-model="showModal"
      :no-outside-dismiss="true"
      id="searchExternalPatientModal"
      ref="searchExternalPatientModal"
      :cancelText="$t('modals.buttons.back')"
      :show-okay="false"
      size="medium"
      @cancel="handleCancel"
      :fixed-layout="true">

      <div slot="header" class="text-center mb-0">
        <h3 style="color: black; font-weight: bolder"> {{ $t('ranu.records.search_external_patient') }} </h3>
      </div>

      <form class="evollu-form pt-0">
        <div class="row">
          <div class="col-1"/>
          <div class="col-10">
            <label class="form-label">{{ $t('ranu.records.birthdate') }}</label>
            <DatePicker
              id="date-picker-birthdate"
              v-model="form.data.birthdate"
              :placeholder="$t('ranu.records.birthdate')"
              :config="{ enableTime: false, dateFormat: 'Y-m-d', maxDate: moment.now() }"
              :clearable="true"
              @input="form.errors.clear('birthdate')"
              :error="form.errors.has('birthdate')"
              :error-messages="[$t(form.errors.get('birthdate'))]"
            />
          </div>
          <div class="col-10"/>
        </div>
        <div class="row">
          <div class="col-1"/>
            <div class="col-10">
              <label class="form-label">{{ $t('users.inputs.entity') }}</label>
              <multiselect
                v-model="form.data.entity"
                :options="entities"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                placeholder="---"
                :searchable="true"
                :allow-empty="true"
                :close-on-select="true">
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
            </div>
          <div class="col-1"/>
        </div>
        <div class="row">
          <div class="col-1"/>
          <div class="col-10">
            <label class="form-label">{{ $t('ranu.records.process_id') }}</label>
            <va-input
              id="record-process-id-input"
              :placeholder="$t('ranu.records.process_id')"
              v-model="form.data.process_id"
              :maxlength="255"
              @input="form.errors.clear('process_id')"
              :error="form.errors.has('process_id')"
              :error-messages="[$t(form.errors.get('process_id'))]"
            />
          </div>
          <div class="col-1"/>
        </div>

        <div class="row">
          <div class="col-1"/>
          <div class="col-10">
            <label class="form-label">{{ $t('ranu.records.associated_contact') }}</label>
            <va-input
              id="record-contact-input"
              :placeholder="$t('ranu.records.insert_phone_or_email')"
              v-model="form.data.contact"
              :maxlength="255"
              @input="form.errors.clear('contact')"
            />
          </div>
          <div class="col-1"/>
        </div>

        <div class="row" v-if="form.errors.has('contact')">
          <div class="col-1"/>
          <div class="col-10 alert alert-warning" role="alert">
            <span class="i-evollu-warning-black"/> {{$t(form.errors.get('contact'))}}
          </div>
          <div class="col-1"/>
        </div>


        <div class="text-center">
          <va-button v-if="!form.isLoading" @click.prevent="handleOk">
            {{secondTrial ? $t('ranu.records.external_try_again') : $t('ranu.records.external_send_validation_code') }}
          </va-button>
          <va-button v-if="!form.isLoading && secondTrial" @click.prevent="handleAssociateNewContact">
            {{ $t('ranu.records.external_associate_new_contact') }}
          </va-button>
          <LoadingAnimation
            v-if="form.isLoading"
            :size="'5px'"
            class="d-flex justify-content-center pt-4 pb-4"/>
        </div>

      </form>

    </va-modal>

    <CodeSentModal
      v-if="showCodeSentModal"
      :record="form.record"
      :access_code="form.access_code"
      @canceled="handleCodeSentModalCanceled"
      @consult="handleConsult"/>

    <ContactNotAssociatedModal
      v-if="showContactNotAssociatedModal"
      :birthdate="form.data.birthdate"
      :contact="form.data.contact"
      :process_id="form.data.process_id"
      :associate_new_contact="associateNewContact"
      @consult="handleConsult"
      @canceled="handleContactNotAssociatedCanceled"
    />

  </div>

</template>

<script>
import DatePicker from '@/components/common/DatePicker'
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import CodeSentModal from '@/components/ranu/records/external-patient-modals/CodeSentModal'
import ContactNotAssociatedModal from '@/components/ranu/records/external-patient-modals/ContactNotAssociatedModal'
import { AuthenticationService } from '@/api/RanuService'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'

const errorsPrefix = 'ranu.records.errors.'

export default {
  props:{
    entities: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  components: {
    Multiselect,
    CodeSentModal,
    ContactNotAssociatedModal,
    DatePicker,
    LoadingAnimation,
  },
  data () {
    return {
      showModal: false,
      showCodeSentModal: false,
      showContactNotAssociatedModal: false,
      secondTrial: false,
      associateNewContact: false,
      form: {
        access_code: null,
        data: {
          birthdate: null,
          process_id: null,
          contact: null,
          entity: null
        },
        errors: new Errors(),
        isLoading: false,
        record: null,
      }
    }
  },
  mounted () {
    this.showModal = true
    this.secondTrial = false
  },
  methods: {
    handleCancel: function () {
      this.showModal = false
      setTimeout(() => {
        this.$emit('canceled')
      }, 250)
    },
    handleOk: async function () {
      this.form.isLoading = true
      const ranuService = this.$store.state.services.find(service => service.url === this.$store.state.ranu_server)
      if (!ranuService) {
        return
      }

      const formData = {
        ...this.form.data,
        entity: (this.form.data.entity ? this.form.data.entity.id : null)
      }

      try {
        try {
          const validateExternalRecord = await AuthenticationService.validateExternalRecord(formData.process_id, formData.entity, formData.birthdate)
        } catch (error) {
          this.showToast(this.$t('ranu.records.external_record.invalid'), this.$store.state.errorToastOptions)
          return
        }

        const response = await AuthenticationService.authenticate(formData)
        const data = response.data
        if (!(data && data.access_code && data.record)) {
          return
        }
        this.form.record = data.record
        this.form.access_code = data.access_code
        this.showModal = false
        this.showCodeSentModal = true
      } catch (e) {
        const errors = e.response.data.errors || e.response.data
        this.form.errors.record(errors, errorsPrefix)

        const contact = this.form.errors.get('contact')
        const onlyContactErrors = this.form.errors.getAll('^(?!(?:contact)$)\\w+$').length === 0
        if (onlyContactErrors && contact && (contact.includes('the_contact_is_not_validated_or_associated'))) {
          this.secondTrial=true
          //this.showContactNotAssociatedModal = true
        }
      } finally {
        this.form.isLoading = false
      }
    },
    handleAssociateNewContact: function () {
      this.associateNewContact = true
      this.showContactNotAssociatedModal = true
    },
    handleCodeSentModalCanceled: function () {
      this.showModal = true
      this.showCodeSentModal = false
    },
    handleContactNotAssociatedCanceled: function () {
      this.showModal = true
      this.showContactNotAssociatedModal = false
    },
    handleConsult: function (record) {
      this.form.errors.clear()
      this.showCodeSentModal = false
      this.showModal = false
      setTimeout(() => {
        this.$emit('consult', record)
      }, 250)
    }
  }
}
</script>

<style scoped>

</style>
